import { Typography } from '@mui/material';
import { isFixedProduct } from '@weft/services';
import type { Currency, FixedProduct, FixedProductInput } from '@weft/types';
import { raise, validateNotEmptyArray } from '@weft/utils';
import cn from 'classnames';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Divider } from '../../../../../components/design/divider/Divider';
import { ChargeProvider } from '../charge-context';
import type { NewPlanForm } from '../CreatePlanForm/create-plan-form-types/new-plan-form';
import { CreatePlanSection } from '../CreatePlanSection';
import { FixedCharge } from './fixed-charges-components/FixedCharge';

export type FixedChargesProps = {
  editable?: boolean;
  fixedProducts: FixedProduct[] | FixedProductInput[];
  currency: Currency;
};

export const FixedCharges: React.FC<FixedChargesProps> = ({
  editable = false,
  fixedProducts,
  currency,
}) => {
  const { getValues } = useFormContext<NewPlanForm>();
  const billingData = getValues('billingData');

  return (
    <CreatePlanSection
      hideCreateAnother
      title={
        editable ? undefined : (
          <Typography
            fontSize={16}
            fontWeight={400}
            color="rgba(27,61,85,0.60)"
          >
            Fixed Fees
          </Typography>)}
    >
      <div
        className={cn(
          {
            'rounded-md border border-line shadow-[0px_10px_24px_0px_rgba(0,0,0,0.03)] bg-white':
              !editable,
          },
          { 'space-y-5.5': editable },
        )}
      >
        {fixedProducts.map((product, currentProductIndex) => {
          const { variants } = product;
          validateNotEmptyArray(variants);

          if (!isFixedProduct<FixedProduct>(product)) {
            return raise(
              `Unknown fixed product type: ${JSON.stringify(product, null, 2)}`,
            );
          }

          return (
            <React.Fragment key={product.id}>
              {currentProductIndex > 0 && !editable && (
                <div className="px-8">
                  <Divider className="h-[2px]" />
                </div>
              )}

              <ChargeProvider
                value={{
                  item: product,
                  itemIndex: currentProductIndex,
                  editable,
                  currency,
                }}
              >
                <FixedCharge
                  billingData={billingData}
                  product={product}
                  productIndex={currentProductIndex}
                  editable={editable}
                  currency={currency}
                />
              </ChargeProvider>
            </React.Fragment>
          );
        })}
      </div>
    </CreatePlanSection>
  );
};
